// Coordinates traced on the existing 1586 × 992 road artwork: y, center x, road width.
// Every word uses this one track, projected through the actual responsive image crop.
export const ROAD = [
  [405, 998, 25], [420, 987, 28], [438, 1008, 34],
  [456, 976, 40], [474, 937, 47], [493, 918, 54],
  [511, 927, 59], [530, 953, 65], [551, 1002, 75],
  [576, 1036, 91], [600, 1006, 108], [624, 919, 122],
  [647, 824, 137], [668, 762, 154], [689, 748, 173],
  [712, 782, 198], [736, 862, 222], [762, 941, 255],
  [790, 994, 310], [820, 995, 420], [852, 928, 605],
  [889, 827, 805], [936, 792, 1100], [980, 792, 1300],
];

// A close procession shares the road, then lands in reading order and holds together.
export const TIMING = { drop: 0.48, travel: 4.2, stagger: 0.42, dock: 0.65, hold: 4.5, fade: 0.9, blank: 2.4 };
export const DESCENT = TIMING.drop + TIMING.travel;
export const LAST_ARRIVAL = DESCENT + TIMING.dock + TIMING.stagger * 3;
export const STACK_COMPLETE = LAST_ARRIVAL;
export const FADE_START = STACK_COMPLETE + TIMING.hold;
export const CYCLE = FADE_START + TIMING.fade + TIMING.blank;
const clamp = (n, min = 0, max = 1) => Math.max(min, Math.min(max, n));
const mix = (a, b, t) => a + (b - a) * t;
const ease = (t) => { t = clamp(t); return t * t * (3 - 2 * t); };
const glide = (t) => { t = clamp(t); return t*t*t*(t*(t*6-15)+10); };
const blendQuads = (a, b, t) => a.map((point, i) => point.map((value, axis) => mix(value, b[i][axis], t)));
// Match the incoming velocity and finish at rest. Unlike restarting an ease-in,
// this keeps the words moving through the travel/landing handoff.
function landWithMomentum(origin, target, velocity, duration, t) {
  return origin.map((point, i) => point.map((value, axis) => {
    const distance = target[i][axis] - value;
    const tangent = velocity[i][axis] * duration;
    // Near-aligned landings need a gentle settling curve, not an extremely
    // high-power ease that brakes almost instantly at the centered position.
    if (Math.abs(distance) < Math.max(0.001, Math.abs(tangent) * 0.25) || tangent * distance < 0) {
      return mix(value, target[i][axis], ease(t)) + tangent * t * Math.pow(1-t, 3);
    }
    const slope = tangent / distance;
    const power = Math.max(2, Math.ceil(slope));
    // A monotone ease with the exact incoming slope, including very short landings.
    const progress = 1 - Math.pow(1-t, power) * (1 + (power-slope)*t);
    return value + distance * progress;
  }));
}
const DEFAULT_ASPECTS = [269/125, 245/125, 147/125, 348/125];

export function arrivalQuads(geometry, aspects = geometry.aspects || DEFAULT_ASPECTS) {
  const gap = geometry.width * 0.018;
  const letterHeight = Math.min(geometry.width * 0.84 / (aspects.reduce((sum, a) => sum+a, 0) + 0.54), geometry.viewportHeight * 0.12);
  const totalWidth = letterHeight * aspects.reduce((sum, a) => sum+a, 0) + gap * 3;
  const bottom = geometry.height - Math.max(24, geometry.height * 0.035);
  let x = (geometry.width - totalWidth) / 2;
  return aspects.map(aspect => {
    const w = letterHeight * aspect;
    const quad = [[x,bottom-letterHeight],[x+w,bottom-letterHeight],[x+w,bottom],[x,bottom]];
    x += w + gap;
    return quad;
  });
}

export function roadAt(y) {
  y = clamp(y, ROAD[0][0], ROAD[ROAD.length - 1][0]);
  const i = Math.max(0, ROAD.findIndex((point) => point[0] >= y) - 1);
  const a = ROAD[i], b = ROAD[Math.min(i + 1, ROAD.length - 1)];
  const t = clamp((y - a[0]) / (b[0] - a[0]));
  const previous = ROAD[Math.max(0, i - 1)], next = ROAD[Math.min(ROAD.length - 1, i + 2)];
  // Hermite interpolation follows the bends without corners or separate per-word paths.
  const m0 = (b[1] - previous[1]) / (b[0] - previous[0]);
  const m1 = (next[1] - a[1]) / (next[0] - a[0]);
  const x = (2*t*t*t - 3*t*t + 1)*a[1] + (t*t*t - 2*t*t + t)*(b[0]-a[0])*m0
    + (-2*t*t*t + 3*t*t)*b[1] + (t*t*t-t*t)*(b[0]-a[0])*m1;
  return { y, x, width: mix(a[2], b[2], t) };
}

export function projectRoad(y, geometry) {
  const point = roadAt(y);
  const { width, height, scale, offsetX, offsetY } = geometry;
  // The broad foreground road meets the viewer in the center of the screen.
  const foreground = ease((y - 850) / 90);
  const x = mix(point.x * scale + offsetX, width / 2, foreground);
  const visibleWidth = 2 * Math.max(1, Math.min(x - 12, width - 12 - x));
  return {
    x, y: point.y * scale + offsetY,
    width: Math.min(point.width * scale * 0.94, visibleWidth, width * 0.88),
    height,
  };
}

export function roadQuad(progress, aspect, geometry) {
  const arrival = arrivalQuads(geometry)[0];
  const maxHeight = arrival[2][1] - arrival[0][1];
  // Exit into the clear foreground above the waiting phrase, never through it.
  const roadEnd = geometry.roadEnd ?? clamp((arrival[0][1] - maxHeight * 0.12 - geometry.offsetY) / geometry.scale, 790, 936);
  const y = mix(405, roadEnd, clamp(progress));
  const center = projectRoad(y, geometry);
  // Upright faces ride on the road, rather than being flattened onto its surface.
  // A small turn-dependent lean and tapered top retain perspective and readability.
  const back = projectRoad(y - 8, geometry), front = projectRoad(y + 8, geometry);
  const turn = clamp((front.x - back.x) / Math.max(1, front.y - back.y), -1, 1);
  const widestAspect = Math.max(...(geometry.aspects || DEFAULT_ASPECTS));
  // All words share the same letter height at a given depth; long words set the width limit.
  const rawHeight = center.width / widestAspect;
  const height = rawHeight / Math.pow(1 + Math.pow(rawHeight / maxHeight, 4), 0.25);
  const width = height * aspect;
  const topWidth = width * mix(0.97, 1, clamp(progress));
  const lean = turn * (width - topWidth) * 0.22;
  return [
    [center.x - topWidth / 2 + lean, center.y - height],
    [center.x + topWidth / 2 + lean, center.y - height],
    [center.x + width / 2, center.y], [center.x - width / 2, center.y],
  ];
}

export function dropQuad(progress, aspect, geometry) {
  const entrance = projectRoad(ROAD[0][0], geometry);
  const tip = geometry.logoTip || { x: entrance.x, y: entrance.y - geometry.height * 0.06 };
  const destination = roadQuad(0, aspect, geometry);
  const width = (destination[2][0] - destination[3][0]) * 0.55;
  const start = [[tip.x-width/2,tip.y-width/aspect],[tip.x+width/2,tip.y-width/aspect],[tip.x+width/2,tip.y],[tip.x-width/2,tip.y]];
  return blendQuads(start, destination, glide(progress));
}

export function listQuads(geometry, aspects) {
  const { width, height, viewportHeight } = geometry;
  const weights = [1, 1, 0.5, 1];
  const gapRatio = 0.01;
  const totalRatio = weights.reduce((sum, weight) => sum + weight, 0) + gapRatio * 3;
  const letterHeight = Math.min(width * 0.74 / Math.max(...aspects), (geometry.typeHeight || height) * 0.30 / totalRatio, viewportHeight * 0.60 / totalRatio);
  const gap = letterHeight * gapRatio;
  const totalHeight = letterHeight * totalRatio;
  // Center every final row on the page; preserve its size and vertical position.
  const centerX = width / 2;
  // Extend only the landing area; the artwork crop and shared road track stay fixed.
  let y = height + (geometry.landingSpace || 0) - Math.max(24, height * 0.035) - totalHeight;
  return aspects.map((aspect, i) => {
    const h = letterHeight * weights[i], w = h * aspect, x = centerX - w / 2;
    const quad = [[x, y], [x+w, y], [x+w, y+h], [x, y+h]];
    y += h + gap;
    return quad;
  });
}

export function sequenceFrame(time, index, aspect, geometry, target) {
  // Keep the final reading order, but release the bottom row first.
  const releaseIndex = 3 - index;
  const local = time - releaseIndex * TIMING.stagger;
  if (local < TIMING.drop) {
    return { quad: dropQuad(local / TIMING.drop, aspect, geometry), opacity: ease(local / 0.12), phase: 'drop' };
  }
  const landingStart = TIMING.drop + TIMING.travel * 0.68;
  if (local < landingStart) {
    return { quad: roadQuad(Math.pow(clamp((local - TIMING.drop) / TIMING.travel), 1.15), aspect, geometry), opacity: 1, phase: 'travel' };
  }
  if (local < DESCENT + TIMING.dock) {
    // Leave the last bend directly for this row, with no sideways line-up or reordering.
    const origin = roadQuad(Math.pow(0.68, 1.15), aspect, geometry);
    const step = 0.0001;
    const before = roadQuad(Math.pow(0.68-step/TIMING.travel, 1.15), aspect, geometry);
    const after = roadQuad(Math.pow(0.68+step/TIMING.travel, 1.15), aspect, geometry);
    const velocity = before.map((point,i)=>point.map((value,axis)=>(after[i][axis]-value)/(2*step)));
    const duration = DESCENT + TIMING.dock - landingStart;
    const t = clamp((local-landingStart)/duration);
    return {quad:landWithMomentum(origin,target,velocity,duration,t),opacity:1,phase:'stack'};
  }
  if (time < FADE_START) return { quad: target, opacity: 1, phase: 'list' };
  return { quad: target, opacity: 1-ease((time-FADE_START)/TIMING.fade), phase: time < FADE_START + TIMING.fade ? 'fade' : 'pause' };
}

// A readable sky stack introduces the same road journey, without changing its
// bends, close stagger, or centered landing. Each row leaves its own stack slot.
export const SKY_HOLD = 3;
export const SHARED_CYCLE = CYCLE + SKY_HOLD - TIMING.blank + 0.3;
export function skyQuads(geometry, aspects) {
  const rows = listQuads(geometry, aspects);
  const top = geometry.skyTop ?? Math.max(24, geometry.viewportHeight * 0.06);
  const origin = rows[0][0][1];
  const widest = Math.max(...rows.map(row => row[1][0] - row[0][0]));
  const stackHeight = rows[rows.length - 1][2][1] - origin;
  // Keep the introduction inside the open upper-left sky, with space for its
  // soft reveal. Only the starting stack shrinks; road and landing stay intact.
  const skyScale = Math.min(1, geometry.width * 0.40 / widest, geometry.viewportHeight * 0.24 / stackHeight);
  const center = geometry.skyLeft == null ? geometry.width / 2 : geometry.skyLeft + widest * skyScale / 2;
  return rows.map(row => row.map(([x, y]) => [(x - geometry.width / 2) * skyScale + center, (y - origin) * skyScale + top]));
}

export function sharedSequenceFrame(time, index, aspect, geometry, target) {
  const start = skyQuads(geometry, geometry.aspects)[index];
  const local = time - SKY_HOLD - (3 - index) * TIMING.stagger;
  if (local < 0) {
    // Match the sky claims' soft reveal: 700ms fade, 900ms blur and gentle lift.
    const revealTime = Math.max(0, time - index * 0.7);
    const remaining = 1 - ease(revealTime / 0.9);
    const anchorX = geometry.skyLeft ?? geometry.width / 2;
    const anchorY = geometry.skyTop ?? Math.max(24, geometry.viewportHeight * 0.06);
    const quad = start.map(([x, y]) => [anchorX + (x-anchorX) * (1 + 0.04*remaining), anchorY + (y-anchorY) * (1 + 0.04*remaining) + 16*remaining]);
    return { quad, opacity: ease(revealTime / 0.7), blur: 15*remaining, phase: 'sky-stack' };
  }
  if (local < TIMING.drop) {
    return { quad: blendQuads(start, roadQuad(0, aspect, geometry), glide(local / TIMING.drop)), opacity: 1, phase: 'drop' };
  }
  return sequenceFrame(time - SKY_HOLD, index, aspect, geometry, target);
}

// Project an untransformed text rectangle onto an upright perspective face.
export function matrixForQuad(quad, width, height) {
  const [p0, p1, p2, p3] = quad;
  const dx1=p1[0]-p2[0], dx2=p3[0]-p2[0], dx3=p0[0]-p1[0]+p2[0]-p3[0];
  const dy1=p1[1]-p2[1], dy2=p3[1]-p2[1], dy3=p0[1]-p1[1]+p2[1]-p3[1];
  const denominator=dx1*dy2-dx2*dy1;
  const g=Math.abs(denominator)>1e-9 ? (dx3*dy2-dx2*dy3)/denominator : 0;
  const h=Math.abs(denominator)>1e-9 ? (dx1*dy3-dx3*dy1)/denominator : 0;
  const a=p1[0]-p0[0]+g*p1[0], b=p3[0]-p0[0]+h*p3[0];
  const d=p1[1]-p0[1]+g*p1[1], e=p3[1]-p0[1]+h*p3[1];
  return [a/width,d/width,0,g/width,b/height,e/height,0,h/height,0,0,1,0,p0[0],p0[1],0,1];
}

const instances = new WeakMap();

async function setupSharedRoad(trail) {
  if (instances.has(trail)) return;
  const stage = trail.querySelector('.gt-stage');
  const image = stage?.querySelector('.gt-landscape--world img');
  const waypoint = trail.querySelector('.gt-waypoint--road');
  if (!stage || !image || !waypoint) return;
  instances.set(trail, true);
  await Promise.all([document.fonts.ready, document.fonts.load('100px "Pop Crispies Chewy"').catch(() => {}), image.decode().catch(() => {})]);
  if (!trail.isConnected) return;
  const layer = document.createElement('div');
  layer.className = 'gt-shared-road';
  layer.setAttribute('aria-hidden', 'true');
  const words = ['chewy', 'gooey', 'and', 'delicious'].map(text => {
    const word = document.createElement('span');
    word.className = 'pc-road-sequence__word';
    word.textContent = text;
    layer.append(word);
    return word;
  });
  const shadows = words.map(() => {
    const shadow = document.createElement('span');
    shadow.className = 'pc-road-sequence__shadow';
    layer.append(shadow);
    return shadow;
  });
  stage.append(layer);
  let geometry, targets, sizes, active = false, elapsed = 0, previous = 0, raf = 0, cameraSettled = false, cameraWidth = 0;
  function updateCamera() {
    cameraWidth = stage.clientWidth;
    const top = waypoint.getBoundingClientRect().top;
    // Begin pulling back while the last landmark leaves; reach the final camera
    // before the animation pane arrives. This reverses naturally on upward scroll.
    const reveal = ease((innerHeight * 1.2 - top) / innerHeight);
    trail.style.setProperty('--pc-road-reveal', String(reveal));
  }
  function measure() {
    const box = stage.getBoundingClientRect(), picture = image.getBoundingClientRect();
    const [ox, oy] = getComputedStyle(image).objectPosition.split(' ').map(parseFloat);
    const scale = Math.max(picture.width / 1586, picture.height / 992);
    const referenceHeight = box.height;
    const landingSpace = 0;
    // The image and all word positions share exactly one viewport-sized pane.
    const cameraHeight = parseFloat(getComputedStyle(image).height);
    const imageBottom = picture.bottom - box.top;
    const height = box.height;
    trail.style.setProperty('--pc-shared-image-bottom', `${imageBottom}px`);
    trail.style.setProperty('--pc-shared-road-height', `${Math.max(cameraHeight, height + landingSpace)}px`);
    geometry = {
      width: box.width, height, typeHeight: referenceHeight, landingSpace, viewportHeight: innerHeight, scale,
      offsetX: picture.left - box.left + (picture.width - 1586 * scale) * ox / 100,
      offsetY: picture.top - box.top + (picture.height - 992 * scale) * oy / 100,
    };
    sizes = words.map(word => ({width: word.offsetWidth, height: word.offsetHeight}));
    geometry.aspects = sizes.map(size => size.width / size.height);
    const claims = stage.querySelector('.gt-cloud-claims')?.getBoundingClientRect();
    if (claims) {
      geometry.skyLeft = claims.left - box.left;
      geometry.skyTop = claims.top - box.top;
    }
    // Keep the finished phrase in the visible foreground, not below the screen.
    targets = listQuads({ ...geometry, height: Math.min(height, innerHeight), landingSpace: 0 }, geometry.aspects);
    // Preserve the exact original artwork-space drift and bend-to-landing point,
    // rather than shortening it to fit the new viewport crop.
    const referenceScale = Math.max(box.width / 1586, referenceHeight / 992);
    const reference = { ...geometry, height: referenceHeight };
    const arrival = arrivalQuads(reference)[0];
    const maxHeight = arrival[2][1] - arrival[0][1];
    const referenceOffsetY = (referenceHeight - 992 * referenceScale) / 2;
    geometry.roadEnd = clamp((arrival[0][1] - maxHeight * 0.12 - referenceOffsetY) / referenceScale, 790, 936);
    const entry = projectRoad(405, geometry);
    geometry.logoTip = {x: entry.x, y: entry.y - 16 * scale};
  }
  function render() {
    if (!geometry) return;
    words.forEach((word, index) => {
      if (elapsed < 0) {
        word.style.opacity = '0';
        shadows[index].style.opacity = '0';
        word.dataset.phase = 'claim-exit';
        return;
      }
      const size = sizes[index];
      const frame = sharedSequenceFrame(elapsed % SHARED_CYCLE, index, size.width / size.height, geometry, targets[index]);
      word.style.filter = `blur(${frame.blur || 0}px)`;
      word.style.transform = `matrix3d(${matrixForQuad(frame.quad, size.width, size.height).join(',')})`;
      word.style.opacity = String(frame.opacity);
      const anchorX = (frame.quad[2][0] + frame.quad[3][0]) / 2;
      const anchorY = (frame.quad[2][1] + frame.quad[3][1]) / 2;
      const shadowWidth = (frame.quad[2][0] - frame.quad[3][0]) * 1.06;
      const shadowHeight = Math.max(2, (frame.quad[3][1] - frame.quad[0][1]) * 0.16);
      shadows[index].style.transform = `translate(${anchorX-shadowWidth/2}px,${anchorY-shadowHeight*0.75}px) scale(${shadowWidth/100},${shadowHeight/20})`;
      shadows[index].style.opacity = String(frame.opacity * (frame.phase === 'drop' ? 0.12 : 0.26));
      word.style.zIndex = String(10 + Math.round(Math.max(...frame.quad.map(point => point[1])) / geometry.height * 100));
      word.dataset.phase = frame.phase;
    });
  }
  function tick(now) {
    raf = 0;
    if (!trail.isConnected || !active || document.hidden) { previous = 0; return; }
    if (previous) {
      const dt = Math.min(0.1, (now - previous) / 1000);
      // Once the scene is visible, complete the entire phrase without requiring
      // another scroll. The former scroll gate stranded CHEWY at 0.7 seconds.
      elapsed += dt;
    }
    previous = now;
    if (!cameraSettled) { measure(); cameraSettled = elapsed >= 0; }
    render();
    raf = requestAnimationFrame(tick);
  }
  function update(event) {
    const rect = waypoint.getBoundingClientRect();
    const noteBottom = trail.querySelector('[data-key="soft"] .gt-note')?.getBoundingClientRect().bottom ?? 0;
    const next = event.detail?.active ?? (rect.top < innerHeight * 0.65 && noteBottom < innerHeight * 0.45 && rect.bottom > 0);
    const enabled = next && trail.hasAttribute('data-enhanced');
    // The claim fades for 700ms; keep every road word hidden until it is gone.
    // Only delay entry, not subsequent animation loops.
    updateCamera();
    if (enabled !== active || cameraWidth !== stage.clientWidth) {
      cameraSettled = false;
    }
    if (enabled && !active) { elapsed = -0.8; previous = 0; }
    active = enabled;
    trail.toggleAttribute('data-road-active', active);
    layer.style.opacity = active ? '1' : '0';
    if (active) { measure(); render(); }
    if (active && !raf && !document.hidden) raf = requestAnimationFrame(tick);
    if (!active || document.hidden) { cancelAnimationFrame(raf); raf = 0; previous = 0; }
  }
  const controller = new AbortController();
  trail.addEventListener('gt:road-update', update, {signal: controller.signal});
  document.addEventListener('visibilitychange', update, {signal: controller.signal});
  trail.addEventListener('shopify:section:unload', () => {
    controller.abort(); cancelAnimationFrame(raf); layer.remove(); trail.removeAttribute('data-road-active'); trail.style.removeProperty('--pc-road-reveal'); instances.delete(trail);
  }, {once: true});
  measure();
  update({});
}

if (typeof document !== 'undefined') {
  const setup = () => {
    document.querySelectorAll('gooey-trail:has(.gt-waypoint--road)').forEach(setupSharedRoad);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
  else setup();
  document.addEventListener('shopify:section:load', setup);
}
